import { Fieldset, Grid, Select, Space, TextInput } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import {
	IconBuildingStore,
	IconTextCaption,
	IconUsers,
} from '@tabler/icons-react';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { query } from '../../../util/api/query';
import { organisationNameToUrl } from '../../../util/convertToUrl';
import type { DropdownOptionDto } from '../../../util/types/DropdownOptionDto';
import type { SelectableBranchDto } from '../../../util/types/SelectableBranchDto';
import { PasswordInputs } from '../../organisation-accounts/components/PasswordInputs';

type UpsertStockAccountFormProps = {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	form: UseFormReturnType<any, (values: any) => any>;
	organisationId: string;
	isEdit?: boolean;
};

export function UpsertStockAccountForm({
	form,
	organisationId,
	isEdit = false,
}: UpsertStockAccountFormProps) {
	const organisationsQuery = query<DropdownOptionDto[]>('organisation/options');

	const selectedOrganisationName = organisationsQuery.data?.value.find(
		(o) => o.id === organisationId,
	)?.name;

	// The branches live in the selected organisation's tenant database, so they can
	// only be listed once an organisation has been chosen.
	const branchesQuery = query<SelectableBranchDto>(
		`${organisationNameToUrl(selectedOrganisationName ?? '')}/branches/options`,
		!!selectedOrganisationName,
	);

	return (
		<>
			<Fieldset legend={'Information'}>
				<Grid>
					<Grid.Col span={4}>
						<TextInput
							label={'First Name'}
							placeholder={'Insert first name...'}
							leftSection={<IconTextCaption {...defaultIconStyle} />}
							key={form.key('firstName')}
							{...form.getInputProps('firstName')}
							data-cy="firstName-input"
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						<TextInput
							label={'Last Name'}
							placeholder={'Insert last name...'}
							leftSection={<IconTextCaption {...defaultIconStyle} />}
							key={form.key('lastName')}
							{...form.getInputProps('lastName')}
							data-cy="lastName-input"
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						<TextInput
							label={'Email'}
							placeholder={'Insert email...'}
							leftSection={<IconTextCaption {...defaultIconStyle} />}
							key={form.key('email')}
							{...form.getInputProps('email')}
							data-cy="email-input"
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<Select
							label={'Organisation'}
							placeholder={'Select organisation...'}
							data={organisationsQuery.data?.value.map((o) => ({
								label: o.name,
								value: o.id,
							}))}
							searchable
							leftSection={<IconUsers {...defaultIconStyle} />}
							key={form.key('organisationId')}
							{...form.getInputProps('organisationId')}
							onChange={(value) => {
								form.setFieldValue('organisationId', value ?? '');
								form.setFieldValue('branchId', '');
							}}
							data-cy="organisationId-input"
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<Select
							label={'Branch'}
							placeholder={
								organisationId
									? 'Select branch...'
									: 'Select an organisation first...'
							}
							data={branchesQuery.data?.value.branches.map((b) => ({
								label: b.name,
								value: b.id.toString(),
							}))}
							searchable
							disabled={!organisationId}
							leftSection={<IconBuildingStore {...defaultIconStyle} />}
							key={form.key('branchId')}
							{...form.getInputProps('branchId')}
							data-cy="branchId-input"
						/>
					</Grid.Col>
				</Grid>
			</Fieldset>
			<Space h={20} />
			<Fieldset legend={'Sign-In Credentials'}>
				<Grid>
					<Grid.Col span={12}>
						<TextInput
							label={'Username'}
							placeholder={'Insert username...'}
							leftSection={<IconTextCaption {...defaultIconStyle} />}
							key={form.key('username')}
							{...form.getInputProps('username')}
							data-cy="username-input"
						/>
					</Grid.Col>
					{!isEdit && <PasswordInputs form={form} inline />}
				</Grid>
			</Fieldset>
		</>
	);
}
